import React, { useState, useEffect, useContext } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
	getResponse,
	saveResponse,
	validatePID,
	logActivity,
} from "../services/dashApiService";
import { BackButton } from "./common/BackButton";
import { NextButton } from "./common/NextButton";
import { ConfirmationButton } from "./common/ConfirmationButton";
import { QuestionnaireInfoPage } from "./QuestionnaireInfoPage";
import { QuestionnaireQuestionPage } from "./QuestionnaireQuestionPage";
import { QuestionnaireQuestionWithTextPage } from "./QuestionnaireQuestionWithTextPage";
import { QuestionnaireQuestionWithMonthYearPage } from "./QuestionnaireQuestionWithMonthYearPage";
import { QuestionnaireHospitalPage } from "./QuestionnaireHospitalPage";
import { QuestionnaireDrugTreatmentPage } from "./QuestionnaireDrugTreatmentPage";
import { QuestionnaireStagingPage } from "./QuestionnaireStagingPage";
import { QuestionnaireMultiSelectPage } from "./QuestionnaireMultiSelectPage";
import { QuestionnaireMultiSelectWithTextPage } from "./QuestionnaireMultiSelectWithTextPage";
import { QuestionnaireNurseDetailsPage } from "./QuestionnaireNurseDetailsPage";
import { QuestionnaireConditionalDatePage } from "./QuestionnaireConditionalDatePage";
import { QuestionnaireStartPageHeader } from "./QuestionnaireStartPageHeader";
import { QuestionnairePageHeader } from "./QuestionnairePageHeader";
import { QuestionnaireScalePage } from "./QuestionnaireScalePage";
import { QuestionnaireSelectMultiplePage } from "./QuestionnaireSelectMultiplePage";
import { QuestionnaireWeight } from "./QuestionnaireWeight";
import { QuestionnaireInput } from "./QuestionnaireInput";
import { QuestionnaireTrafficLight } from "./QuestionnaireTrafficLight";
import { GenericModal } from "./common/GenericModal";
import { HelpPopup } from "./HelpPopup";
import { ModalDialog } from "./common/ModalDialog";
import {
	CodesToColorMapFunc,
	BgColorToTextColorMapFunc,
} from "../CodesToColorMap";
import { ReportCodesToHelpCodesMap } from "../ReportCodesToHelpCodesMap";
import { NavFooter } from "./common/NavFooter";
import { LoadingSpinnerAlt } from "./common/LoadingSpinnerAlt";
import { ErrorModal } from "./common/ErrorModal";
import { Transition } from "@headlessui/react";
import { TransitionConfig } from "../configs/transitionConfig";
import { QuestionnaireHorizontalScalePage } from "./QuestionnaireHorizontalScalePage";
import { DiseaseGroupContext } from "../contexts/diseaseGroupContext";
import { QuestionnaireEmailNhsPage } from "./QuestionnaireEmailNhsPage";
import { QuestionnairePid } from "./QuestionnairePid";
import { QuestionnairePageTextPanel } from "./common/QuestionnairePageTextPanel";
import { toast } from "react-toastify";
import { validateNhsno } from "../utils";

export const Questionnaire = () => {
	const location = useLocation();
	const defaultErrorMessage = [
		"Unfortunately we have had a problem. It's nothing that you have done.",
		"Our support team is aware and will be working on this. We apologise for any inconvenience.",
		"Press 'OK' to return to the main screen.",
		"If the problem presists, please contact our helpdesk (details available in the Setup dialogue) and quote reference '#Q001'",
	];
	const { id, code, from: fromLocation, bgColor } = location?.state ?? {};

	const history = useHistory();

	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);
	const [version, setVersion] = useState();

	const [response, setResponse] = useState();
	const [questionnaire, setQuestionnaire] = useState();

	const [currentResponse, setCurrentResponse] = useState(null);
	const [pageHistory, setPageHistory] = useState([]);
	const [shadowPageHistory, setShadowPageHistory] = useState([]);
	const [existingHistoryItem, setExistingHistoryItem] = useState(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [branchedFrom, setBranchedFrom] = useState(null);

	const [isShowingPrompt, setIsShowingPrompt] = useState(false);
	const [customPromptMessage, setCustomPromptMessage] = useState(null);
	const [isShowingError, setIsShowingError] = useState(false);

	const [isShowingStartPage, setIsShowingStartPage] = useState(false);
	const [isShowingQuestionnairePage, setIsShowingQuestionnairePage] =
		useState(false);
	const [isPagingForward, setIsPagingForward] = useState(true);

	const [currentPageDefinition, setCurrentPageDefinition] = useState();

	const [isShowingHelp, setIsShowingHelp] = useState(false);

	const [showInformationDialogue, setShowInformationDialogue] = useState(false);
	const [information, setInformation] = useState([]);
	const [message, setMessage] = useState();

	const incompleteAnswers = ["needHelp", "refuseToRecord"]; // a list of answers which stop the questionnaire from being recorded as complete

	const [helpCode, setHelpCode] = useState(ReportCodesToHelpCodesMap[code]);

	useEffect(() => {
		setIsLoading(true);
		getResponse(id, code)
			.then((result) => {
				if (result.success) {
					const data = result.data;
					setResponse(data);
					setVersion(
						`Version ${data?.defaults?.displayVersion || data.version}`
					);

					if (data.defaults.helpCode) {
						setHelpCode(data.defaults.helpCode);
					}

					if (data.questionnaires && data.questionnaires.length > 0) {
						const ques = data.questionnaires[0];

						setQuestionnaire(ques);

						if (ques.allowIncompleteSaving === true && data.responseHistory) {
							const responseHistory = data.responseHistory.find(
								(e) => e.id == ques.id
							).history;

							if (responseHistory) {
								setPageHistory(responseHistory);
								setShadowPageHistory(responseHistory);
								setCurrentResponse(responseHistory[0]);
								setExistingHistoryItem(responseHistory[0]);
							}
						}
					} else {
						setHasError(true);
					}
					setIsLoading(false);
				} else {
					if (result.err) {
						console.error(`${result.err.code}: ${result.err.msg}`);
					} else {
						console.error("Unknown error occurred calling 'getResponse'");
					}
					setHasError(true);
					setIsLoading(false);
				}
			})
			.catch((error) => {
				console.error(error);
				setHasError(true);
				setIsLoading(false);
			});
	}, []);

	useEffect(() => {
		if (currentResponse !== null) {
			if (existingHistoryItem) {
				if (existingHistoryItem.response !== currentResponse) {
					let pHistory = [...pageHistory];
					const indexToReplace = pHistory.findIndex(
						(item) => item.page === currentPage
					);

					pHistory[indexToReplace].response = currentResponse;

					// 'Future' currently being destroyed here if *ANY* response in the 'past' is changed
					pHistory = pHistory.slice(0, indexToReplace + 1);

					// ==============================
					// === If requirements change ===
					// ==============================
					// const branchedItem = pHistory.find((item) => item.branchedFrom);

					// if (branchedItem && branchedItem.page > currentPage) {
					//   // 'Future' can be destroyed here if an option that was branched to exists in the future

					//   if (branchedItem.branchedFrom === currentPage) {
					//     // 'Future' can be destroyed here, specifically when an item in the future was branched to,
					//     // because of a response to this specific question (ORIGINAL REQUIREMENT)
					//   }
					// }

					setPageHistory(pHistory);
				}
			}
		}
		if (isShowingPrompt === true) {
			setIsShowingPrompt(false);
			setCurrentPage(currentPage + 1);
		}
	}, [currentResponse]);

	useEffect(() => {
		const hItem = pageHistory.find((e) => e.page === currentPage);
		const shItem = shadowPageHistory.find((e) => e.page === currentPage);

		if (hItem) {
			if (branchedFrom) {
				hItem.branchedFrom = branchedFrom;
				setBranchedFrom(null);
			}

			setExistingHistoryItem(hItem);
		} else {
			const newHistoryItem = {
				page: currentPage,
			};

			const pHistory = [...pageHistory];
			pHistory.push(newHistoryItem);
			setPageHistory(pHistory);
		}

		const item = hItem || shItem;

		if (item && item.response) {
			setCurrentResponse(item.response);
		} else {
			setCurrentResponse(null);
		}
	}, [currentPage, pageHistory, branchedFrom, shadowPageHistory]);

	useEffect(() => {
		setIsShowingStartPage(true);
	}, []);

	useEffect(() => {
		setIsShowingQuestionnairePage(false);
		setIsShowingStartPage(false);

		setTimeout(() => {
			if (currentPage === 0) {
				setCurrentPageDefinition(null);
				setIsShowingStartPage(true);
			} else {
				setCurrentPageDefinition(questionnaire.def[currentPage - 1]);
				setIsShowingQuestionnairePage(true);
			}
		}, TransitionConfig.slideXTransition.enterDuration);
	}, [currentPage, questionnaire]);

	const handleCurrentResponse = (response) => {
		setCurrentResponse(response);
	};

	const handleBack = () => {
		setIsPagingForward(false);

		if (currentPage === 0) {
			if (
				questionnaire.allowIncompleteSaving === true &&
				pageHistory.length > 1
			) {
				handlePageReturn();
			} else {
				handlePageReturn();
			}
		} else if (existingHistoryItem.branchedFrom) {
			setCurrentPage(existingHistoryItem.branchedFrom);
		} else {
			setCurrentPage(currentPage - 1);
		}
	};

	const branch = (pageId) => {
		let indexOfBranchToPage = questionnaire.def.findIndex((page) => {
			return page.id === pageId;
		});

		if (indexOfBranchToPage == null) {
			indexOfBranchToPage = currentPage;
		}

		setBranchedFrom(currentPage);
		setCurrentPage(indexOfBranchToPage + 1);
	};

	const handleNext = () => {
		// reset prompt message
		setCustomPromptMessage(null);
		setIsPagingForward(true);

		let currentResponseValue;
		if (currentResponse) {
			if (Array.isArray(currentResponse.value)) {
				currentResponseValue = currentResponse.value.join(",") || "";
			} else if (typeof currentResponse.value === "object") {
				currentResponseValue =
					currentResponse.value.value ||
					currentResponse.value.option ||
					(currentResponse.value.options
						? currentResponse.value.options.join(",")
						: "") ||
					"";
			} else {
				currentResponseValue = currentResponse.value;
			}
		}

		if (currentPage === questionnaire.def.length) {
			handleFinish();
		} else if (
			currentPage === 0 ||
			currentPageDefinition.type === "info" ||
			currentPageDefinition.type === "nurse-details" ||
			((currentPageDefinition.type === "scale" ||
				currentPageDefinition.type === "horizontal-scale") &&
				currentResponse)
		) {
			setCurrentPage(currentPage + 1);
		} else if (currentPageDefinition.type === "email_nhs") {
			// email_nhs: Custom validation
			const patientEmail = currentResponse?.value?.email;
			const patientNhsNo = currentResponse?.value?.nhsNo;
			// Check all required data is present
			if (
				patientEmail.length > 0 &&
				patientNhsNo.length > 0 &&
				currentResponse?.value?.consentDate > 0
			) {
				// Check if NhsNo is valid
				if (!validateNhsno(patientNhsNo.replace(/[^0-9]/g, ""))) {
					setCustomPromptMessage("Please enter a valid NHS/CHI no.");
					setIsShowingPrompt(true);
				} else {
					// Check if email is valid
					validatePID(
						patientEmail.toLowerCase(),
						patientNhsNo.replace(/[^0-9]/g, "")
					).then((res) => {
						const resData = res?.data;
						if (res?.success) {
							if (resData.message) {
								let message = resData.message.map((m, index) => {
									return <p key={`msgline_${index}`}>{m}</p>;
								});

								// note the use of TOAST to avoid disrupting the flow for the page
								toast(<>{message}</>, {
									type: "warning",
								});
							}
							setCurrentPage(currentPage + 1);
						} else {
							setCustomPromptMessage(resData.message);
							setIsShowingPrompt(true);
						}
					});
				}
			} else {
				setCustomPromptMessage(
					"You must fill out all required fields before continuing."
				);
				setIsShowingPrompt(true);
			}
		} else if (currentPageDefinition.type === "pid") {
			// pid: Custom validation
			// select all required fields
			const { mobilePhone, dob, cancerName, ...pidRequired } =
				currentResponse?.value || {};
			// Check if required data is missing or form state is invalid
			if (
				!Object.values(pidRequired).every((value) => Boolean(value + "")) ||
				!currentResponse?.valid
			) {
				setCustomPromptMessage(
					"You must fill out all required fields before continuing."
				);
				setIsShowingPrompt(true);
			} else {
				setCurrentPage(currentPage + 1);
			}
		} else if ("weight" === currentPageDefinition.type) {
			if (
				currentResponse?.value?.option == null &&
				!currentPageDefinition.isResponseRequired
			) {
				setCurrentPage(currentPage + 1);
			} else if (currentResponse?.value?.option) {
				if (
					currentPageDefinition.maxValue != null ||
					currentPageDefinition.minValue != null
				) {
					const min = (currentPageDefinition.minValue = null
						? -Infinity
						: currentPageDefinition.minValue);
					const max = (currentPageDefinition.maxValue = null
						? Infinity
						: currentPageDefinition.maxValue);
					const val = parseInt(currentResponse.value.option, 10);
					if (val >= min && val <= max) {
						setCurrentPage(currentPage + 1);
					} else {
						setCustomPromptMessage(currentPageDefinition.nullResponseError);
						setIsShowingPrompt(true);
					}
				} else {
					setCurrentPage(currentPage + 1);
				}
			} else {
				setCustomPromptMessage(currentPageDefinition.nullResponseError);
				setIsShowingPrompt(true);
			}
		} else if ("questionWithMonthYear" === currentPageDefinition.type) {
			let issue = false;
			if (!(currentResponseValue || "").trim() || currentResponse.value.error) {
				issue = true;
			}

			if (issue) {
				setCustomPromptMessage(currentPageDefinition.nullResponseError);
				setIsShowingPrompt(true);
			} else {
				const indexOfOption = currentPageDefinition.options.findIndex(
					(option) => option.value.toString() === currentResponseValue //parseInt(currentResponseValue)
				);

				if (
					currentPageDefinition.options[indexOfOption] &&
					currentPageDefinition.options[indexOfOption].hasOwnProperty(
						"branchTo"
					)
				) {
					branch(currentPageDefinition.options[indexOfOption].branchTo);
				} else {
					setCurrentPage(currentPage + 1);
				}
			}
		} else if ("hospital-selector" === currentPageDefinition.type) {
			if (currentResponseValue && currentResponseValue.trim()) {
				setCurrentPage(currentPage + 1);
			} else {
				setIsShowingPrompt(true);
			}
		} else if ("input" === currentPageDefinition.type) {
			if (
				(currentResponseValue && currentResponseValue.trim()) ||
				!currentPageDefinition.isResponseRequired
			) {
				let defaultBranchTo = currentPageDefinition.defaultBranchTo;

				if (defaultBranchTo) {
					branch(defaultBranchTo);
				} else {
					setCurrentPage(currentPage + 1);
				}
			}
		} else if ("trafficLight" === currentPageDefinition.type) {
			let defaultBranchTo = currentPageDefinition.defaultBranchTo;

			if (defaultBranchTo) {
				branch(defaultBranchTo);
			} else {
				setCurrentPage(currentPage + 1);
			}
		} else if ("cancer-staging" === currentPageDefinition.type) {
			let defaultBranchTo = currentPageDefinition.defaultBranchTo;

			if (defaultBranchTo) {
				branch(defaultBranchTo);
			} else {
				setCurrentPage(currentPage + 1);
			}
		} else if (
			["multi-select", "selectmultiple", "multiselect"].indexOf(
				currentPageDefinition.type
			) >= 0
		) {
			if (
				(currentResponseValue && currentResponseValue.trim()) ||
				!currentPageDefinition.isResponseRequired
			) {
				let defaultBranchTo = currentPageDefinition.defaultBranchTo;

				let branchTo;
				for (const ropt of currentResponse.value) {
					if (branchTo) {
						break;
					}

					for (const dopt of currentPageDefinition.options) {
						if (dopt.value === ropt && dopt.branchTo) {
							branchTo = dopt.branchTo;
							break;
						}
					}
				}

				if (branchTo || defaultBranchTo) {
					branch(branchTo || defaultBranchTo);
				} else {
					setCurrentPage(currentPage + 1);
				}
			} else {
				if (currentPageDefinition.isResponseRequired) {
					setIsShowingPrompt(true);
				}
			}
		} else if (
			["multiselectWithText"].indexOf(currentPageDefinition.type) >= 0
		) {
			if (
				(currentResponseValue && currentResponseValue.trim()) ||
				!currentPageDefinition.isResponseRequired
			) {
				let defaultBranchTo = currentPageDefinition.defaultBranchTo;

				let branchTo;
				const _opts = currentResponse?.value?.options || [];
				for (const ropt of _opts) {
					if (branchTo) {
						break;
					}

					for (const dopt of currentPageDefinition.options) {
						if (dopt.value.toString() === ropt.toString() && dopt.branchTo) {
							branchTo = dopt.branchTo;
							break;
						}
					}
				}

				if (branchTo || defaultBranchTo) {
					branch(branchTo || defaultBranchTo);
				} else {
					setCurrentPage(currentPage + 1);
				}
			} else {
				if (currentPageDefinition.isResponseRequired) {
					setIsShowingPrompt(true);
				}
			}
		} else if (
			currentResponseValue &&
			currentResponseValue.toString() !== "-1"
		) {
			const indexOfOption = currentPageDefinition.options.findIndex(
				(option) => option.value.toString() === currentResponseValue //parseInt(currentResponseValue)
			);

			if (
				currentPageDefinition.options[indexOfOption] &&
				currentPageDefinition.options[indexOfOption].hasOwnProperty("branchTo")
			) {
				branch(currentPageDefinition.options[indexOfOption].branchTo);
				// const branchToGuid =
				// 	currentPageDefinition.options[indexOfOption].branchTo;
				// const indexOfBranchToPage = questionnaire.def.findIndex((page) => {
				// 	return page.id === branchToGuid;
				// });

				// setBranchedFrom(currentPage);
				// setCurrentPage(indexOfBranchToPage + 1);
			} else {
				setCurrentPage(currentPage + 1);
			}
		} else if (currentResponse && currentResponseValue === -1) {
			setCurrentPage(currentPage + 1);
		} else if (currentPageDefinition.hidePrompt) {
			setCurrentPage(currentPage + 1);
		} else {
			if (currentPageDefinition.isResponseRequired) {
				setCustomPromptMessage(
					currentPageDefinition.nullResponseError ||
						questionnaire.nullResponseError ||
						null
				);
				setIsShowingPrompt(true);
			} else if (
				currentPageDefinition.nullResponseWarning ||
				questionnaire.nullResponseWarning
			) {
				setCustomPromptMessage(
					currentPageDefinition.nullResponseWarning ||
						questionnaire.nullResponseWarning
				);
				setIsShowingPrompt(true);
			} else {
				setCurrentPage(currentPage + 1);
			}
		}
	};

	const closeInformationDialogue = () => {
		logActivity("info", "ack side-effects popup", message);
		setShowInformationDialogue(false);
		setInformation([]);
		setMessage();
		handlePageReturn();
	};

	const handleSaveAndQuit = (isComplete) => {
		const def = [...questionnaire.def];

		pageHistory.forEach((pageItem) => {
			if (pageItem.response) {
				const response = pageItem.response;
				const defItem = def.find((defItem) => defItem.id === response.id);
				defItem.answer = response.value;

				let answer = defItem.answer;
				if (Array.isArray(answer)) {
					answer = answer.join(",");
				} else if (typeof answer === "object") {
					answer = answer.option;
				}

				if (incompleteAnswers.indexOf(answer) >= 0) {
					isComplete = false;
				}
			}
		});

		setIsLoading(true);
		setIsShowingQuestionnairePage(false);
		saveResponse(id, code, def, isComplete, pageHistory)
			.then((response) => {
				if (response?.data?.message?.msg) {
					const info = response.data.message.msg.map((m, index) => {
						const ret = [];
						switch (m.type) {
							case "list":
								ret.push(
									<ul className="list-disc ml-6" key={`ul_${index}`}>
										{m.body.map((b, index2) => {
											return (
												<li
													key={`ul_${index}_${index2}`}
													dangerouslySetInnerHTML={{ __html: b }}
												></li>
											);
										})}
									</ul>
								);
								break;

							case "link":
								ret.push(
									<p
										className={`${m.class || "mb-2"}`}
										dangerouslySetInnerHTML={{
											__html: `<span class="ss-icon ss-link text-md text-blue-600"}></span><span class="text-blue-600">${m.body}</span>`,
										}}
										onClick={() => {
											window.open(m.url, "_blank");
										}}
									></p>
								);
								break;

							case "text":
							default:
								for (const b of m.body) {
									ret.push(
										<p
											className="mb-2"
											key={`p_${index}`}
											dangerouslySetInnerHTML={{ __html: b }}
										></p>
									);
								}
								break;
						}

						return (
							<div key={`messageContainer_${index}`} className="w-full mb-3">
								{ret}
							</div>
						);
					});

					setInformation(info);
					setMessage(response.data.message);
					setShowInformationDialogue(true);
					setIsLoading(false);
				} else {
					setTimeout(() => {
						setIsLoading(false);
						handlePageReturn();
					}, TransitionConfig.slideXTransition.leaveDuration);
				}
			})
			.catch((error) => {
				setIsLoading(false);
				setIsShowingQuestionnairePage(true);
				setIsShowingError(true);
				setErrorMessage([
					"There was an error trying to save your details - probably because of a problem with the connection to our server.",
					"Press 'OK' to close this message so that you can try again, once you have checked that you have a good connection to the internet.",
					"Press 'Cancel' to close this dialogue and return to the main screen - unfortunately your changes will be lost.",
					"If the problem presists, please contact our helpdesk (details available in the Setup dialogue) and quote reference '#Q002'",
				]);
			});
	};

	const handleFinish = () => {
		handleSaveAndQuit(true);
	};

	const hideFinalBackButton = () => {
		if (currentPageDefinition) {
			if (questionnaire && questionnaire.hideFinalBackButton) {
				const lastPageInDef = questionnaire.def[questionnaire.def.length - 1];

				return currentPageDefinition.id === lastPageInDef.id;
			}
		}

		return false;
	};

	const handlePageReturn = () => {
		if (fromLocation) {
			history.push({
				pathname: fromLocation.path,
				state: {
					id: id,
					code: fromLocation.code,
					from: {
						path: location.pathname,
					},
				},
			});
		} else {
			history.push("/dashboard");
		}
	};

	const handleModalNext = () => {
		setCurrentResponse({
			id: currentPageDefinition.id,
			value: -1,
		});
	};

	const { theme } = useContext(DiseaseGroupContext);

	const [currentTheme, setCurrentTheme] = useState(null);
	useEffect(() => {
		const color = CodesToColorMapFunc(code, theme) ?? theme.primary();
		setCurrentTheme(color);
	}, []);

	const textColor = BgColorToTextColorMapFunc(currentTheme, "text");

	const renderPageDefinition = () => {
		if (!isShowingQuestionnairePage) {
			return <></>;
		}

		if (currentPageDefinition) {
			switch (currentPageDefinition.type) {
				case "info":
					return (
						<QuestionnaireInfoPage
							def={currentPageDefinition}
							variant={
								currentPage === questionnaire.def.length ? "complete" : null
							}
						/>
					);
				case "question":
					return (
						<QuestionnaireQuestionPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "questionWithMonthYear":
					return (
						<QuestionnaireQuestionWithMonthYearPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "questionWithText":
					return (
						<QuestionnaireQuestionWithTextPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "input":
					return (
						<QuestionnaireInput
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "email_nhs":
					return (
						<QuestionnaireEmailNhsPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "pid":
					return (
						<QuestionnairePid
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "multiselect":
				case "multi-select":
					return (
						<QuestionnaireMultiSelectPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "multiselectWithText":
					return (
						<QuestionnaireMultiSelectWithTextPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "selectmultiple":
					return (
						<QuestionnaireSelectMultiplePage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "cancer-staging":
					return (
						<QuestionnaireStagingPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "conditional-date":
					return (
						<QuestionnaireConditionalDatePage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "hospital-selector":
					return (
						<QuestionnaireHospitalPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "nurse-details":
					return (
						<QuestionnaireNurseDetailsPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "weight":
					return (
						<QuestionnaireWeight
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "trafficLight":
					return (
						<QuestionnaireTrafficLight
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "drug-treatment":
					return (
						<QuestionnaireDrugTreatmentPage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
						/>
					);
				case "scale":
					return (
						<QuestionnaireScalePage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
							code={code}
						/>
					);
				case "horizontal-scale":
					return (
						<QuestionnaireHorizontalScalePage
							def={currentPageDefinition}
							onRespond={handleCurrentResponse}
							currentResponse={currentResponse}
							code={code}
						/>
					);
				default:
					currentPageDefinition.isResponseRequired = false;
					currentPageDefinition.hidePrompt = true;
					currentPageDefinition.defaultBranchTo = false;
					return (
						<QuestionnairePageTextPanel
							text={[
								"This question requires an update to your app.",
								"Please visit your app store and download the latest version of	Your Health Companion",
							]}
							hasBackground={true}
							hasBorder={false}
							isCentered={true}
						/>
					);
			}
		}
	};

	const currentThemeColorBg =
		bgColor || (currentTheme ? currentTheme.bg() : "");
	let qLen = 0;
	if (questionnaire && questionnaire.def) {
		qLen = questionnaire.def.length;
	}

	const nextButtonVariant =
		currentPage === qLen
			? "navigationText" //"active"
			: "navigationText";
	const nextButtonOverride = currentPage === qLen ? "Finish" : null;
	const backButtonVariant =
		currentPage === qLen
			? "navigationText" //"active"
			: "navigationText";

	return (
		<div className={"fixed inset-0"}>
			{isLoading ? (
				<div className={`flex flex-col w-full h-full`}>
					<LoadingSpinnerAlt isFixed={true} color={textColor} />
				</div>
			) : hasError ? (
				<>
					<QuestionnaireStartPageHeader
						title={questionnaire.name}
						preTitle={questionnaire.preTitle}
						onHelpClick={helpCode ? () => setIsShowingHelp(true) : null}
						code={code}
						background={currentThemeColorBg}
						textColor={`text-black`}
					/>
					<div className={"flex-grow overflow-y-auto bg-white"}>
						<div
							className={
								"mt-6 md:mt-6 lg:mt-8 xl:mt-12 mb-28 max-w-panel mx-auto px-6 md:px-0"
							}
						>
							{errorMessage.map((m, index) => {
								const cls = index === errorMessage.length - 1 ? "mb-6" : "mb-3";
								return (
									<p key={`qerror_${index}`} className={`text-left ${cls}`}>
										{m}
									</p>
								);
							})}
							<BackButton
								onClick={handlePageReturn}
								showButton={false}
								textOverride={"OK"}
								variant={"active"}
							/>
						</div>
					</div>
				</>
			) : showInformationDialogue ? (
				<ModalDialog show={showInformationDialogue} onClose={() => {}}>
					<div
						className={`relative bg-white text-black flex flex-col transition-all transform max-w-xl shadow-lg p-3 sm:p-4 rounded-lg`}
					>
						{information}
						{message.confirm ? (
							<div className="flex justify-items-center mt-3">
								<div className="w-full">
									<ConfirmationButton
										onClick={closeInformationDialogue}
										variant="active"
										showIcon={false}
										textOverride="OK"
										text={message.confirm}
									/>
								</div>
							</div>
						) : (
							<div className="flex justify-around mt-3">
								<div className="w-32">
									<BackButton
										onClick={closeInformationDialogue}
										variant="active"
										showIcon={false}
										textOverride="OK"
									/>
								</div>
							</div>
						)}
					</div>
				</ModalDialog>
			) : response && questionnaire ? (
				<>
					<Transition
						className={`bg-white flex flex-col w-full h-full relative`}
						appear={true}
						show={isShowingStartPage}
						enter={`transform ease-in-out duration-${TransitionConfig.slideXTransition.enterDuration}`}
						enterFrom={
							isPagingForward ? "translate-x-full" : "-translate-x-full"
						}
						enterTo={"translate-x-0"}
						leave={`transition-opacity ease-in-out duration-${TransitionConfig.slideXTransition.leaveDuration}`}
						leaveFrom={"opacity-100"}
						leaveTo={"opacity-0"}
					>
						{/* Header */}
						<QuestionnaireStartPageHeader
							title={questionnaire.name}
							preTitle={questionnaire.preTitle}
							onHelpClick={helpCode ? () => setIsShowingHelp(true) : null}
							code={code}
							background={currentThemeColorBg}
							textColor={`text-black`}
						/>

						{/* Main content */}
						<div
							className={
								"flex flex-grow flex-col max-w-panel mx-auto text-black pt-4"
							}
						>
							<div>
								{questionnaire.description &&
									!!questionnaire.description.trim() && (
										<p
											className={
												"text-center mx-3.5 mt-6 sm:mt-12 p-4 sm:p-6 bg-gray-100 rounded-xl"
											}
										>
											{questionnaire.description}
										</p>
									)}
								<p className={"text-center text-sm max-w-xxs mx-auto pt-2"}>
									{version || questionnaire.version}
								</p>
							</div>
							<div
								className={
									"max-w-xxs sm:max-w-md mx-auto flex-grow flex items-center"
								}
							>
								<p className={"text-center text-lg"}>
									Press <b>Next</b> to start,
									<br /> otherwise press <b>Back</b> to return
								</p>
							</div>
						</div>

						{/* Footer */}
						<div className={""}>
							<NavFooter
								currentTheme={currentTheme}
								textColor={textColor}
								smallPrint={questionnaire.copyright}
								isBorderOff={true}
							>
								<BackButton
									onClick={handleBack}
									variant={backButtonVariant}
									showIcon={false}
								/>
								<NextButton
									onClick={handleNext}
									variant={nextButtonVariant}
									textOverride={nextButtonOverride}
								/>
							</NavFooter>
						</div>
					</Transition>

					<Transition
						className={`bg-white flex flex-col w-full h-full relative`}
						appear={true}
						show={isShowingQuestionnairePage}
						enter={`transform ease-in-out duration-${TransitionConfig.slideXTransition.enterDuration}`}
						enterFrom={
							isPagingForward ? "translate-x-full" : "-translate-x-full"
						}
						enterTo={"translate-x-0"}
						leave={`transition-opacity ease-in-out duration-${TransitionConfig.slideXTransition.leaveDuration}`}
						leaveFrom={"opacity-100"}
						leaveTo={"opacity-0"}
					>
						<div>
							<QuestionnairePageHeader
								title={questionnaire.bannerTitle}
								textColor={textColor}
								background={currentThemeColorBg}
								currentPage={isShowingQuestionnairePage && currentPage}
								numOfPages={questionnaire.def.length}
							/>
						</div>

						{/* Main */}
						<div className={"flex-grow overflow-y-auto bg-white"}>
							<div
								className={
									"mt-3 md:mt-6 lg:mt-8 xl:mt-12 mb-28 max-w-panel mx-auto"
								}
							>
								{renderPageDefinition()}
							</div>
						</div>

						{/* Footer */}
						<div className={"absolute left-0 right-0 bottom-0"}>
							<NavFooter
								currentTheme={currentTheme}
								textColor={textColor}
								smallPrint={questionnaire.copyright}
								isBorderOff
							>
								{hideFinalBackButton() ? null : (
									<BackButton
										onClick={handleBack}
										showIcon={false}
										variant={backButtonVariant}
										// smaller
									/>
								)}
								<NextButton
									onClick={handleNext}
									variant={nextButtonVariant}
									textOverride={nextButtonOverride}
								/>
							</NavFooter>
						</div>
					</Transition>
					{currentPageDefinition ? (
						<GenericModal
							show={isShowingPrompt}
							onClose={() => setIsShowingPrompt(false)}
							onNext={
								currentPageDefinition.isResponseRequired
									? null
									: handleModalNext
							}
							text={
								(currentPageDefinition.nullResponseMessage
									? currentPageDefinition.nullResponseMessage
									: currentPageDefinition.isResponseRequired
									? response.nullResponseError
									: response.nullResponseWarning) ??
								(customPromptMessage ||
									"Please select an answer before moving to the next question")
							}
							backText={"OK"}
						/>
					) : null}

					<ErrorModal
						show={isShowingError}
						onClose={() => setIsShowingError(false)}
						currentTheme={currentTheme}
						text={errorMessage}
						button2="Cancel"
						button2Click={() => {
							setIsShowingError(false);
							history.push("/dashboard");
						}}
					/>

					<HelpPopup
						show={isShowingHelp}
						onClose={() => setIsShowingHelp(false)}
						currentTheme={currentTheme}
						helpCode={helpCode}
					/>
				</>
			) : (
				<div
					className={`bg-white flex flex-col w-full h-full justify-center px-8`}
				>
					<p className={`text-center mb-16`}>
						There was a problem retrieving the questionnaire details.
					</p>
					<p className={`text-center mb-16`}>Press back to try again.</p>
					<BackButton
						onClick={handlePageReturn}
						variant={"inactive"}
						showButton={false}
					>
						Back
					</BackButton>
				</div>
			)}
		</div>
	);
};
